/**
 * InAppBrowserHint — Soft, dismissable variant of the IAB blocker.
 *
 * Used on LANDING pages (vs. the full-screen blocker on CHECKOUT pages).
 * The intent is to softly pre-warn TikTok/IG/FB IAB visitors that they
 * should open the page in a real browser BEFORE they get deep into the
 * sales pitch, but without blocking their access to the content.
 *
 * Behaviour:
 *   - Renders a slim sticky banner at the top of the page
 *   - Dismissable (X button + the dismissal persists in sessionStorage so
 *     refreshing the same tab won't re-show it)
 *   - Disappears entirely when NOT in an in-app browser
 *
 * Detection mirrors the full-screen blocker (same UA regex list).
 */

import { useEffect, useState } from "react";
import { Info, X } from "lucide-react";

const IAB_PATTERNS = [
  /TikTok/i, /Bytedance/i, /musical_ly/i,
  /Instagram/i, /FBAV/i, /FBAN/i, /FB_IAB/i,
  /Snapchat/i, /LinkedInApp/i, /Twitter/i,
];

const detectIAB = () => {
  if (typeof navigator === "undefined") return null;
  const ua = navigator.userAgent || "";
  for (const re of IAB_PATTERNS) {
    if (re.test(ua)) {
      const isIOS = /iPhone|iPad|iPod/i.test(ua);
      const isAndroid = /Android/i.test(ua);
      const name = re.toString().includes("TikTok") || re.toString().includes("musical")
        ? "TikTok"
        : re.toString().includes("Instagram")
        ? "Instagram"
        : re.toString().includes("FB")
        ? "Facebook"
        : re.toString().includes("Snapchat")
        ? "Snapchat"
        : "in-app browser";
      return { name, isIOS, isAndroid };
    }
  }
  return null;
};

const DISMISS_KEY = "rr_iab_hint_dismissed";

const InAppBrowserHint = () => {
  const [iab, setIab] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (typeof window !== "undefined" && window.sessionStorage?.getItem(DISMISS_KEY)) {
      setDismissed(true);
      return;
    }
    setIab(detectIAB());
  }, []);

  if (!iab || dismissed) return null;

  const tipAr = iab.isIOS
    ? "للحصول على أفضل تجربة دفع، يُفضّل فتح الصفحة في Safari (اضغطي ⋯ ثم « Open in Safari »)"
    : iab.isAndroid
    ? "للحصول على أفضل تجربة دفع، يُفضّل فتح الصفحة في Chrome (اضغطي ⋮ ثم « فتح في Chrome »)"
    : "للحصول على أفضل تجربة دفع، يُفضّل فتح الصفحة في متصفّحكِ الأساسي";

  const dismiss = () => {
    setDismissed(true);
    try { window.sessionStorage?.setItem(DISMISS_KEY, "1"); } catch (_) { /* ignore */ }
  };

  return (
    <div
      className="sticky top-0 z-[80] bg-[#3A1A12] text-[#FFE5B8] border-b border-[#F4B842]/30 shadow-sm"
      data-testid="iab-soft-hint"
      role="status"
      aria-live="polite"
    >
      <div className="max-w-2xl mx-auto px-3 py-2 flex items-start gap-2.5">
        <Info className="w-3.5 h-3.5 mt-1 shrink-0 text-[#F4B842]" />
        <div className="flex-1 text-[11.5px] leading-[1.55]" dir="rtl">
          <span className="font-bold">تنبيه:</span> {tipAr}
        </div>
        <button
          onClick={dismiss}
          className="text-[#F4B842]/80 hover:text-white p-1 -mt-0.5 -mr-1"
          aria-label="إخفاء"
          data-testid="iab-soft-hint-dismiss"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
};

export default InAppBrowserHint;
